import config from '@config'
import useDisableThirdPartyScripts from './useDisableThirdPartyScripts'
import { useInfiniteQuery } from 'react-query'
import { formatDate } from '@utils/date'
import {
  DataPage,
  GetNextPageType,
  GetPostsLeftType,
  GetScribbleDataType,
  ScribbleAPIResult,
  ScribblePost,
  ScribbleRawPost,
  UseScribblePollingProps,
} from '@widgets/Scribble/types'

const {
  scribble: {
    options: { pageSize, pollInterval },
    token: scribbleToken,
  },
} = config

const formatPost = ({
  Id,
  Content,
  Rank,
  IsDeleted,
  CreationDate,
  Icons,
  PostMeta,
}: ScribbleRawPost): ScribblePost => ({
  id: `${Id}`,
  content: Content,
  rank: Rank,
  deleted: IsDeleted,
  dateInfo: formatDate(CreationDate),
  icon: Icons,
  meta: PostMeta,
})

const getNextPage: GetNextPageType = (pagination, pageParam) => {
  if (!pagination?.TotalPages) {
    return
  }
  // If we reach end of the stream, `nextPage` should be undefined.
  // `hasNextPage` will return false if `getNextPageParam` returns undefined.
  return pagination.TotalPages === pageParam + 1 ? undefined : pageParam + 1
}

const getPostsLeft: GetPostsLeftType = (pagination, pageParam) => {
  if (!pagination?.TotalPages) {
    return 0
  }
  return pagination.TotalPosts - (pageParam + 1) * pageSize
}

const getScribbleData: GetScribbleDataType = async ({
  streamId,
  pageParam = 0,
}) => {
  const payload = (await (
    await fetch(
      `https://api.scribblelive.com/v1/stream/${streamId}/posts?token=${scribbleToken}&PageSize=${pageSize}&PageNumber=${pageParam}`
    )
  ).json()) as ScribbleAPIResult

  const { posts } = payload

  if (posts.length > 0) {
    return {
      nextPage: getNextPage(payload?.pagination, pageParam),
      isLive: !!payload?.streamMeta?.IsLive,
      posts: [...posts].slice(0, pageSize).map(formatPost),
      postsLeft: getPostsLeft(payload?.pagination, pageParam),
    }
  } else {
    throw new Error(
      `[useArticleScribblePolling.ts] Invalid scribble data for streamId: ${streamId}`
    )
  }
}

const useArticleScribblePolling = ({
  streamId = '',
}: UseScribblePollingProps) => {
  const disableThirdPartyScripts = useDisableThirdPartyScripts()

  const infiniteQueryResult = useInfiniteQuery<DataPage>(
    ['articleScribblePosts', streamId],
    (fetchParams) => getScribbleData({ ...fetchParams, streamId }),
    {
      enabled: !disableThirdPartyScripts && !!streamId,
      getNextPageParam: (lastPage) => lastPage.nextPage,
      onError: (err) => {
        console.error({
          reason: `[useArticleScribblePolling.ts] Loading ${streamId} failed`,
          error: err,
        })
      },
      refetchInterval: (data) =>
        data?.pages[data.pages.length - 1]?.isLive ? pollInterval : false,
      refetchOnWindowFocus: false,
    }
  )

  return {
    posts: infiniteQueryResult.data?.pages?.reduce(
      (acc, page) => [...acc, ...page.posts],
      [] as ScribblePost[]
    ),
    hasNextPage: infiniteQueryResult.hasNextPage,
    fetchNextPage: infiniteQueryResult.fetchNextPage,
    pageSize,
    postsLeft:
      infiniteQueryResult.data?.pages?.[
        infiniteQueryResult.data.pages.length - 1
      ]?.postsLeft || 0,
    isLoading:
      infiniteQueryResult.isFetching && !infiniteQueryResult.isRefetching,
    isFailed: infiniteQueryResult.isError,
  }
}

export default useArticleScribblePolling
